import {customElement, LitElement, property, PropertyValues} from 'lit-element';
import objectPropParser from '../util/object-prop-parser';
import {loadEmbeddedApp} from './EmbeddedApp';
import constants from '../util/constants';
import MiniAppContainer from './MiniAppContainer/MiniAppContainer';
import React, {useEffect} from 'react';
import ReactDOM from 'react-dom';
import {GlobalShadowStyle, GlobalStyle, Theme} from '@nab/nui-react';
import ErrorScreen from './ErrorScreen/ErrorScreen';
import IdentityApp from './IdentityApp/IdentityApp';
import {EventMgr, KeepAlive} from '@nab/nab-x-sdk-browser';
import axios from 'axios';
import {debug, error} from '../util/logger';
import { getShellActions } from '@nab/x-runtime'
import {getCachedTokens, init} from '../util/init';

/**
 * This custom element will load a mini-app inside itself once inserted in to the DOM
 *
 * Config can be supplied in a serialised string in the 'config' attribute
 *
 * <mini-app-loader config='{ "tag": "foobar-app", "manifest": "http://nab.foo/asset-manifest.json" }'></mini-app-loader>
 */
@customElement('mini-app-loader')
export class MiniAppLoader extends LitElement {

    @property({type: Object, attribute: 'config', converter: objectPropParser})
    public config: any;

    @property({type: String, attribute: 'x-mini-app-id', reflect: true})
    public miniAppId: string;

    @property({type: Boolean, attribute: 'x-mini-app-loaded', reflect: true})
    public loaded: boolean;

    @property({type: String, attribute: 'auth', reflect: true})
    public auth: string;

    protected loadInitiated: boolean = false;

    public async updated(changedProps: PropertyValues) {
        if ((changedProps.size || !this.loadInitiated) && this.config) {
            console.log('MiniApp Loader updated');
            this.renderMiniApp();
        }
    }

    public async connectedCallback() {
        super.connectedCallback();
        if (this.config && !this.loadInitiated) {
            const nabShellScript = document.querySelector('mini-app-loader');
            if (nabShellScript && (nabShellScript as any).nextElementSibling) {
                const MANIFEST_ADDRESS = (nabShellScript as any).nextElementSibling.src.match(/^(.*)index.js/)[1];
                // eslint-disable-next-line no-undef
                __webpack_public_path__ = MANIFEST_ADDRESS; // eslint-disable-line camelcase
            } else {
                const script = document.querySelector('main')?.nextElementSibling;
                if (script) {
                    const MANIFEST_ADDRESS = (script as any).src.match(/^(.*)index.js/)[1];
                    // eslint-disable-next-line no-undef
                    __webpack_public_path__ = MANIFEST_ADDRESS; // eslint-disable-line camelcase
                }
            }
            console.log(`Webpack public path: ${__webpack_public_path__}`);

            this.renderMiniApp();
        }
    }

    /**
     * Loads the mini-app using configurations obtained from AEM.
     *
     * Retrieves the content fragment for the mini-app which will include values used to populate authorable properties
     * of the mini-app. This will be set as a property "appConfig" on the mini-app host element.
     *
     * Set the API configurations based on the current environment, uses default values when configurations are not
     * supplied through the elements attribute from AEM.
     */
    protected async renderMiniApp() {

        if (this.loadInitiated) {
            return;
        }
        this.loadInitiated = true;

        const config = {...this.config};

        const appConfig = await init(config);
        this.miniAppId = config.tag;

        const heartbeat = async (): Promise<boolean> => {
            const url: string = appConfig.shellConfig.kongEsgEndPoint + constants.OAUTH_KEEP_ALIVE_ENDPOINT;
            const tokens = await getCachedTokens();
            for (const token of tokens) {
                try {
                    await axios.get(url,
                        {
                            headers: {
                                authorization: `bearer ${token}`
                            }
                        });
                } catch {
                    error(appConfig, 'Heartbeat call failed');
                }
            }
            return true;
        };

	    const appActions = getShellActions();

	    const keepAlive = new KeepAlive({
            idleTime: appConfig.keepAlive.idleTime,
            heartbeatProvider: heartbeat, onUserInActive: () => {
                debug(appConfig, 'user inactive');
                appActions.dispatchEvent(constants.APP_SHELL_KEEP_ALIVE_USER_INACTIVE);
            }
        });

        const App: React.FunctionComponent<{appConfig: any, renderRoot: any}> = function ({appConfig: any, renderRoot}) {
            useEffect(() => {
                if(!appConfig.disableKeepAlive) {
                    debug(appConfig, 'Init keepalive');
                    keepAlive.init();
                }
                return () => {
                    if(!appConfig.disableKeepAlive) {
                        debug(appConfig, 'destroy keepalive');
                        keepAlive.destroy();
                    }
                };

            }, []);

            return (
                <>
                    <GlobalStyle/>
                    <MiniAppContainer renderRoot={renderRoot}>
                        <Theme>
                            <GlobalShadowStyle borderBox={true} normalise={true}/>
                            <ErrorScreen appConfig={appConfig}/>
                            <IdentityApp appConfig={appConfig} appName={appConfig.tag}
                                         rootElement={this}/>
                        </Theme>
                    </MiniAppContainer>
                </>
            );
        };

        // This config is just to customize the attributes which are mandatory

        if (!customElements.get(constants.APP_SHELL_TAG)) {
            const element: any = document.createElement(constants.APP_SHELL_TAG);
            element.appConfig = appConfig;
            element.user = {};
            element.actions = {};
            this.appendChild(element);
            ReactDOM.render(
                <App
                    appConfig={appConfig}
                    renderRoot={this.renderRoot as any}
                />,
            element);
        }

       if(!appConfig.maintenanceMode) {
           await loadEmbeddedApp(appConfig, this);
       }
    }

    public async disconnectedCallback() {
        super.disconnectedCallback();
    }

    createRenderRoot() {
        return this;
    }
}
